import React, { useState, useEffect, useRef, useCallback } from 'react';
import { createRoot } from 'react-dom/client';
import Table from './Table';
import RequestLogBadge from './RequestLogBadge';
import formatDate from '../helpers/formatDate';
import showInvisibleCharacters from '../helpers/showInvisibleCharacters';

export default function LeadOwnersTable() {
  const [ffData, setFFData] = useState([]);
  const [loadingFFData, setLoadingFFData] = useState(false);
  const [hubspotData, setHubspotData] = useState([]);
  const [loadingHubspotData, setLoadingHubspotData] = useState(false);
  const [loadError, setLoadError] = useState(null);
  const [loadTime, setLoadTime] = useState(null);
  const [requestLog, setRequestLog] = useState([]);
  const [requestLogModalOpen, setRequestLogModalOpen] = useState(false);
  const [emailProblemsModalOpen, setEmailProblemsModalOpen] = useState(false);
  const [modalMessage, setModalMessage] = useState(null);
  const [successToastMessage, setSuccessToastMessage] = useState(null);
  const [syncingRecordIds, setSyncingRecordIds] = useState([]);
  const [bulkSyncConfirmation, setBulkSyncConfirmation] = useState({
    isOpen: false,
    records: [],
  });
  const [bulkSyncState, setBulkSyncState] = useState({
    isOpen: false,
    isRunning: false,
    total: 0,
    completed: 0,
    currentLabel: null,
    failures: [],
  });
  const [syncPreview, setSyncPreview] = useState({
    isOpen: false, isLoading: false, isSaving: false, record: null, fields: null, isNewRecord: false, error: null,
  });
  const activeSyncKeysRef = useRef(new Set());
  const bulkSyncRunningRef = useRef(false);
  const tableInstanceRef = useRef(null);
  // HubSpot account (token id) the current data was loaded for — sent with every sync so
  // the server can refuse it if the user has since flipped the FastLane/CarPool switch
  // (see LeadOwnersController#ensure_same_hubspot_account).
  const loadedHubspotTokenIdRef = useRef(null);

  // Toggles the Sync Status column filter — clicking an already-active status
  // clears it. Reads the current value at click time via the table instance
  // captured from renderToolbar (same approach as BrokersTable).
  const toggleSyncStatusFilter = useCallback((status) => {
    const column = tableInstanceRef.current?.getColumn('sync_status');
    if (!column) return;

    column.setFilterValue((prev) => (prev === status ? undefined : status));
  }, []);

  // Both requests run in parallel. When `log` is set, each one's status, duration and
  // row count go into the request log shown above the table (see BrokersTable, which
  // logs one entry per HubSpot page instead).
  const refreshData = useCallback(async ({ showLoading = true } = {}) => {
    const requests = [
      { requestNumber: 1, label: 'Fastlane lead owners', url: 'api/lead_owners' },
      { requestNumber: 2, label: 'HubSpot owners', url: 'api/lead_owners/hubspot' },
    ];
    const log = requests.map(({ requestNumber, label }) => ({
      requestNumber, label, status: null, ok: null, pending: true, durationMs: null, returned: null, error: null,
    }));
    const startTime = Date.now();

    if (showLoading) {
      setLoadingFFData(true);
      setLoadingHubspotData(true);
      setLoadError(null);
      setLoadTime(null);
      setRequestLog([...log]);
    }

    const runRequest = async ({ requestNumber, url }) => {
      const requestStartedAt = Date.now();
      let response = null;
      let res = null;
      let errorMessage = null;

      try {
        response = await fetch(url);
        res = await response.json();
        if (!response.ok) errorMessage = `HTTP ${response.status}`;
      } catch (err) {
        errorMessage = err.message;
      }

      const index = log.findIndex((entry) => entry.requestNumber === requestNumber);
      log[index] = {
        ...log[index],
        status: response ? response.status : null,
        ok: !errorMessage,
        pending: false,
        durationMs: Date.now() - requestStartedAt,
        returned: Array.isArray(res?.data) ? res.data.length : null,
        error: errorMessage,
      };
      if (showLoading) setRequestLog([...log]);

      if (errorMessage) throw new Error(`Failed to load ${url} (${errorMessage})`);
      return res;
    };

    try {
      const [ffResponse, hubspotResponse] = await Promise.all(requests.map(runRequest));
      setFFData(ffResponse.data);
      setHubspotData(hubspotResponse.data);
      loadedHubspotTokenIdRef.current = ffResponse.meta?.hubspot_token_id ?? null;
    } catch (error) {
      console.error(error);
      if (showLoading) setLoadError('Unable to load lead owner records.');
    } finally {
      if (showLoading) {
        setLoadTime(((Date.now() - startTime) / 1000).toFixed(2));
        setLoadingFFData(false);
        setLoadingHubspotData(false);
      }
    }
  }, []);

  useEffect(() => {
    refreshData();
  }, [refreshData]);

  useEffect(() => {
    if (!successToastMessage) return undefined;

    const timeoutId = window.setTimeout(() => {
      setSuccessToastMessage(null);
    }, 3500);

    return () => window.clearTimeout(timeoutId);
  }, [successToastMessage]);

  const calculateSyncStatus = (ff_record, hs_record) => {
    if (!hs_record  && ff_record) return 'ff_only';
    if (hs_record && !ff_record) return 'hs_only';
    const hs_value = formatDate(hs_record?.updatedAt);
    const ff_value = formatDate(ff_record?.hs_lastmodifieddate);
    if (hs_value === ff_value && hs_record?.archived && !ff_record?.is_active) return 'ff_disabled_synced';
    if (hs_value === ff_value) return 'synced';
    return 'out_of_sync';
  }

    // Iterate FF data and try to find a matching hubspot recrod by HS ID.
  // If no direct match, check if the FF record's hubspot_id is part of any merged records in Hubspot
  const data = ffData.map((owner) => {
    // Attempt to find a direct match in Hubspot data using the hubspot_id from FF record
    const matchingHubspotData = hubspotData.find((hs) => hs.id?.toString() === owner.hubspot_id?.toString());
    
    return {
      ff_record: owner,
      hs_record: matchingHubspotData,
      sync_status: calculateSyncStatus(owner, matchingHubspotData),
    };
  });

  // One entry per active lead owner with an email that would break emails sent to them
  // (invites) or from them (broker updates fall back to the default sender). HubSpot is
  // the source of truth, so its email is what's checked whenever there's a HubSpot
  // record; the Fastlane copy only matters when HubSpot is already correct — a re-sync
  // fixes it — or there's no HubSpot record. Same approach as BrokersTable.
  const emailProblemRows = data.flatMap((row) => {
    const owner = row.ff_record;
    if (!owner || !owner.is_active) return [];

    const hubspotProblems = row.hs_record?.email_problems || [];
    if (hubspotProblems.length > 0) return [{ row, fix: 'hubspot', problems: hubspotProblems }];

    const fastlaneProblems = owner.email_problems || [];
    if (fastlaneProblems.length > 0) {
      return [{ row, fix: row.hs_record ? 'resync' : 'no_hubspot_record', problems: fastlaneProblems }];
    }
    return [];
  });

  const statusCounts = data.reduce((acc, row) => {
    acc[row.sync_status] = (acc[row.sync_status] || 0) + 1;
    return acc;
  }, {});

  const getRecordKey = useCallback((record) => {
    if (record.ff_record?.id) return `ff-${record.ff_record.id}`;
    if (record.hs_record?.id) return `hs-${record.hs_record.id}`;
    if (record.ff_record?.hubspot_id) return `hubspot-${record.ff_record.hubspot_id}`;
    return 'unknown-record';
  }, []);

  const getRecordLabel = useCallback((record) => {
    return (
      record.ff_record?.name ||
      `${record.hs_record?.firstName || ''} ${record.hs_record?.lastName || ''}`.trim() ||
      record.ff_record?.emailID ||
      record.hs_record?.email ||
      record.ff_record?.hubspot_id ||
      record.hs_record?.id ||
      'Unknown lead owner'
    );
  }, []);

  const canSyncRecord = useCallback((record) => {
    return ['synced', 'out_of_sync', 'hs_only'].includes(record.sync_status) && Boolean(record.hs_record?.id);
  }, []);

  const performSyncRequest = useCallback(async (record, options = {}) => {
    const { showSuccessToast = false, showErrorModal = false } = options;

    try {
      const response = await fetch(`api/lead_owners/${record.hs_record.id}/sync_with_hubspot`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]').getAttribute('content') },
        body: JSON.stringify({ hubspot_token_id: loadedHubspotTokenIdRef.current }),
      });

      if (response.ok) {
        if (showSuccessToast) {
          setSuccessToastMessage('The lead owner was successfully synced with HubSpot.');
        }

        return { success: true };
      }

      const errorData = await response.json().catch(() => null);
      const message = errorData?.errors?.join('\n') || `Sync failed (HTTP ${response.status})`;

      if (showErrorModal) {
        setModalMessage(message);
      }

      return { success: false, message };
    } catch (error) {
      console.error('Sync error:', error);
      const message = 'An unexpected error occurred while syncing.';

      if (showErrorModal) {
        setModalMessage(message);
      }

      return { success: false, message };
    }
  }, []);

  const sendSyncRequest = useCallback(async (record) => {
    if (bulkSyncRunningRef.current) return null;

    const recordKey = getRecordKey(record);
    if (activeSyncKeysRef.current.has(recordKey)) return null;

    activeSyncKeysRef.current.add(recordKey);
    setSyncingRecordIds((prev) => (prev.includes(recordKey) ? prev : [...prev, recordKey]));

    try {
      const result = await performSyncRequest(record, { showSuccessToast: true, showErrorModal: true });
      if (result?.success) {
        await refreshData({ showLoading: false });
      }
      return result;
    } finally {
      activeSyncKeysRef.current.delete(recordKey);
      setSyncingRecordIds((prev) => prev.filter((id) => id !== recordKey));
    }
  }, [getRecordKey, performSyncRequest, refreshData]);

  const openSyncPreview = useCallback(async (record) => {
    if (bulkSyncRunningRef.current) return;

    setSyncPreview({
      isOpen: true,
      isLoading: true,
      isSaving: false,
      record,
      fields: null,
      isNewRecord: false,
      error: null,
    });

    try {
      const response = await fetch(`api/lead_owners/${record.hs_record.id}/sync_preview`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]').getAttribute('content'),
        },
        body: JSON.stringify({ hubspot_token_id: loadedHubspotTokenIdRef.current }),
      });

      const responseData = await response.json().catch(() => null);

      if (!response.ok) {
        const message = responseData?.errors?.join('\n') || `Failed to load preview (HTTP ${response.status})`;
        setSyncPreview((prev) => ({ ...prev, isLoading: false, error: message }));
        return;
      }

      setSyncPreview((prev) => ({
        ...prev,
        isLoading: false,
        fields: responseData?.fields || [],
        isNewRecord: Boolean(responseData?.is_new_record),
      }));
    } catch (error) {
      console.error('Sync preview error:', error);
      setSyncPreview((prev) => ({
        ...prev,
        isLoading: false,
        error: 'An unexpected error occurred while loading the preview.',
      }));
    }
  }, []);

  const closeSyncPreview = useCallback(() => {
    setSyncPreview((prev) => (prev.isSaving ? prev : {
      isOpen: false, isLoading: false, isSaving: false, record: null, fields: null, isNewRecord: false, error: null,
    }));
  }, []);

  const confirmSyncPreview = useCallback(async () => {
    const record = syncPreview.record;
    if (!record) return;

    setSyncPreview((prev) => ({ ...prev, isSaving: true }));
    await sendSyncRequest(record);
    setSyncPreview({
      isOpen: false, isLoading: false, isSaving: false, record: null, fields: null, isNewRecord: false, error: null,
    });
  }, [syncPreview.record, sendSyncRequest]);

  const humanizeDiffKey = (key) => key
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (char) => char.toUpperCase());

  const formatDiffValue = (value) => {
    if (value === null || value === undefined || value === '') {
      return <span className="text-muted">—</span>;
    }
    if (typeof value === 'boolean') return value ? 'true' : 'false';
    return String(value);
  };

  const closeBulkSyncModal = useCallback(() => {
    setBulkSyncState((prev) => {
      if (prev.isRunning) return prev;

      return {
        ...prev,
        isOpen: false,
        currentLabel: null,
      };
    });
  }, []);

  const openBulkSyncConfirmation = useCallback((records) => {
    if (bulkSyncRunningRef.current || records.length === 0) return;

    setBulkSyncConfirmation({
      isOpen: true,
      records,
    });
  }, []);

  const closeBulkSyncConfirmation = useCallback(() => {
    setBulkSyncConfirmation({
      isOpen: false,
      records: [],
    });
  }, []);

  const startBulkSync = useCallback(async (records) => {
    if (bulkSyncRunningRef.current || records.length === 0) return;

    bulkSyncRunningRef.current = true;
    setBulkSyncState({
      isOpen: true,
      isRunning: true,
      total: records.length,
      completed: 0,
      currentLabel: null,
      failures: [],
    });

    let failureCount = 0;

    try {
      for (const record of records) {
        const recordKey = getRecordKey(record);
        const recordLabel = getRecordLabel(record);

        activeSyncKeysRef.current.add(recordKey);
        setSyncingRecordIds((prev) => (prev.includes(recordKey) ? prev : [...prev, recordKey]));
        setBulkSyncState((prev) => ({
          ...prev,
          currentLabel: recordLabel,
        }));

        const result = await performSyncRequest(record);

        if (!result?.success) {
          failureCount += 1;
          setBulkSyncState((prev) => ({
            ...prev,
            failures: [...prev.failures, {
              id: recordKey,
              label: recordLabel,
              message: result?.message || 'Unknown sync failure.',
            }],
          }));
        }

        activeSyncKeysRef.current.delete(recordKey);
        setSyncingRecordIds((prev) => prev.filter((id) => id !== recordKey));
        setBulkSyncState((prev) => ({
          ...prev,
          completed: prev.completed + 1,
        }));
      }

      await refreshData({ showLoading: false });
    } finally {
      bulkSyncRunningRef.current = false;
      setBulkSyncState((prev) => ({
        ...prev,
        isRunning: false,
        currentLabel: null,
      }));
      setSuccessToastMessage(`Bulk sync finished. ${records.length - failureCount} succeeded, ${failureCount} failed.`);
    }
  }, [getRecordKey, getRecordLabel, performSyncRequest, refreshData]);

  const confirmBulkSync = useCallback(async () => {
    const records = bulkSyncConfirmation.records;
    closeBulkSyncConfirmation();
    await startBulkSync(records);
  }, [bulkSyncConfirmation.records, closeBulkSyncConfirmation, startBulkSync]);

  const bulkProgressPercent = bulkSyncState.total === 0
    ? 0
    : Math.round((bulkSyncState.completed / bulkSyncState.total) * 100);

  const columns = React.useMemo(
    () => [
      {
        accessorFn: (row) => row.ff_record?.id?.toString() || '',
        header: 'FF ID',
      },
            {
        accessorFn: (row) => row.ff_record?.user_id?.toString() || '',
        header: 'FF User ID',
      },
      {
        accessorFn: (row) => row.hs_record?.id?.toString() || '',
        header: 'HS ID',
      },
      {
        accessorFn: (row) => formatDate(row.ff_record?.hs_lastmodifieddate),
        header: 'FF Version Timestamp',
      },
      {
        accessorFn: (row) => formatDate(row.hs_record?.updatedAt),
        header: 'HS Version Timestamp',
      },
      {
        accessorKey: 'sync_status',
        header: 'Sync Status',
        // Exact match — the default filter fn does substring matching ('synced'
        // would also match 'ff_disabled_synced').
        filterFn: 'equalsString',
        meta: { filterVariant: 'select'},
      },
      {
        accessorKey: 'ff_record.name',
        header: 'FF Name',
      },
      {
        accessorFn: (row) => `${row.hs_record?.firstName || ''} ${row.hs_record?.lastName || ''}`.trim(),
        header: 'HS Name',
      },
      {
        accessorKey: 'ff_record.emailID',
        header: 'FF Email',
      },
      {
        accessorKey: 'hs_record.email',
        header: 'HS Email',
      },
      {
        accessorKey: 'ff_record.is_active',
        accessorFn: (row) => row.ff_record?.is_active ? 'active' : 'inactive',
        header: 'FF Status',
        meta: { filterVariant: 'select'},
      },
      {
        accessorFn: (row) => row.hs_record?.archived ? 'true' : 'false',
        header: 'HS Record Archived',
        meta: { filterVariant: 'select'},
      },
      {
        accessorFn: (row) => row.ff_record?.domain_verification_status || 'unconfigured',
        id: 'domain_verification_status',
        header: 'Sending Domain',
        meta: { filterVariant: 'select'},
        cell: (info) => {
          const status = info.getValue();
          if (status === 'unconfigured') return null;

          const isMismatched = status === 'mismatched';
          return (
            <span
              className="border px-1"
              style={{
                borderRadius: '3px',
                backgroundColor: isMismatched ? 'rgba(255, 0, 0, 0.1)' : 'rgba(0, 128, 0, 0.1)',
                whiteSpace: 'nowrap',
              }}
              title={isMismatched
                ? "This lead owner's email domain does not match any SendGrid-verified domain on their division(s) — emails sent on their behalf may bounce or land in spam."
                : 'Matches a SendGrid-verified sending domain for their division.'}
            >
              {isMismatched ? '⚠ Domain Mismatch' : '✓ Domain Verified'}
            </span>
          );
        },
      },
      {
        id: 'actions',
        header: 'Actions',
        cell: (info) => {
          const record = info.row.original;
          const isRowSyncing = syncingRecordIds.includes(getRecordKey(record));

          if (canSyncRecord(record)) {
            return (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
              <button
                className="btn btn-sm btn-secondary"
                onClick={() => openSyncPreview(record)}
                disabled={bulkSyncState.isRunning || isRowSyncing}
              >
                {isRowSyncing ? 'Syncing...' : 'Sync Record'}
              </button>
              </div>
            );
          }
          return null;  
        }
      }
    ]
  , [bulkSyncState.isRunning, canSyncRecord, getRecordKey, openSyncPreview, syncingRecordIds]);

  return (
    <>
      <div style={{ display: 'flex', gap: '10px', marginBottom: '10px' }}>
        <div style={{ flex: '1 1 0', border: '1px solid #ccc', padding: '10px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          {requestLog.some((entry) => entry.pending) ? (
            <div style={{ padding: '4px 0', color: '#666', fontSize: '14px' }}>
              Loading...
            </div>
          ) : loadTime ? (
            <>
              <div style={{ padding: '4px 0', color: '#666', fontSize: '14px' }}>
                Loaded in {loadTime}s
              </div>
              <button
                type="button"
                className="btn btn-secondary btn-sm"
                style={{ height: '30px' }}
                onClick={() => refreshData()}
                disabled={bulkSyncState.isRunning}
              >
                Reload Data
              </button>
            </>
          ) : null}
        </div>
        <div style={{ flex: '1 1 0', border: '1px solid #ccc', padding: '10px' }}>
          {requestLog.length > 0 && (
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <button
                type="button"
                onClick={() => setRequestLogModalOpen(true)}
                style={{
                  background: 'none',
                  border: 'none',
                  padding: 0,
                  color: '#0d6efd',
                  textDecoration: 'underline',
                  cursor: 'pointer',
                  fontSize: '14px',
                }}
              >
                Request log ({requestLog.length} request{requestLog.length === 1 ? '' : 's'})
              </button>
              <RequestLogBadge requestLog={requestLog} />
            </div>
          )}
        </div>
        <div style={{ flex: '1 1 0', border: '1px solid #ccc', padding: '10px' }}>
          {loadingFFData || loadingHubspotData ? (
            <div style={{ padding: '4px 0', color: '#666', fontSize: '14px' }}>
              Checking lead owner emails...
            </div>
          ) : (
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              {emailProblemRows.length > 0 ? (
                <button
                  type="button"
                  onClick={() => setEmailProblemsModalOpen(true)}
                  style={{
                    background: 'none',
                    border: 'none',
                    padding: 0,
                    color: '#0d6efd',
                    textDecoration: 'underline',
                    cursor: 'pointer',
                    fontSize: '14px',
                  }}
                >
                  Invalid emails ({emailProblemRows.length} lead owner{emailProblemRows.length === 1 ? '' : 's'})
                </button>
              ) : (
                <div style={{ padding: '4px 0', color: '#666', fontSize: '14px' }}>Invalid emails</div>
              )}
              {/* Inline colors for the same reason as RequestLogBadge — the app stylesheet
                  overrides Bootstrap's bg-* utility classes. */}
              {emailProblemRows.length > 0 ? (
                <span className="badge" style={{ backgroundColor: '#dc3545', color: '#fff' }}>
                  {emailProblemRows.length} to fix
                </span>
              ) : (
                <span className="badge" style={{ backgroundColor: '#28a745', color: '#fff' }}>All valid</span>
              )}
            </div>
          )}
        </div>
      </div>

      {emailProblemsModalOpen && (
        <>
          <div className="modal show" style={{ display: 'block' }} tabIndex="-1" role="dialog" aria-modal="true">
            <div className="modal-dialog modal-dialog-centered modal-xl" role="document">
              <div className="modal-content">
                <div className="modal-header bg-danger text-white">
                  <h5 className="modal-title">
                    Invalid emails ({emailProblemRows.length} lead owner{emailProblemRows.length === 1 ? '' : 's'})
                  </h5>
                  <button
                    type="button"
                    className="btn-close btn-close-white"
                    onClick={() => setEmailProblemsModalOpen(false)}
                    aria-label="Close"
                  />
                </div>
                <div className="modal-body" style={{ maxHeight: '65vh', overflowY: 'auto' }}>
                  <p className="text-muted" style={{ fontSize: '13px' }}>
                    These active lead owners have an email the mail server would reject. HubSpot is the
                    source of truth, so fix the email on the HubSpot user, then sync the lead owner here.
                    Until then, broker updates about their leads are sent from the default FastLane address
                    instead of theirs, and invite emails to them fail.
                  </p>
                  <div style={{ overflowX: 'auto' }}>
                    <table className="table table-sm table-bordered" style={{ fontSize: '12px', marginBottom: 0 }}>
                      <thead>
                        <tr>
                          <th>FF ID</th>
                          <th>HS ID</th>
                          <th>Name</th>
                          <th>Value</th>
                          <th>Problem</th>
                          <th>How to fix</th>
                        </tr>
                      </thead>
                      <tbody>
                        {emailProblemRows.flatMap(({ row, fix, problems }) => problems.map((problem, index) => (
                          <tr key={`${row.ff_record.id}-${index}`}>
                            <td>{row.ff_record.id}</td>
                            <td>{row.hs_record?.id || '—'}</td>
                            <td>{getRecordLabel(row)}</td>
                            <td>
                              {fix !== 'hubspot' && <span className="text-muted">FF: </span>}
                              <code>{showInvisibleCharacters(problem.value)}</code>
                            </td>
                            <td>
                              {{
                                whitespace: 'Extra or hidden whitespace',
                                multiple_addresses: 'Multiple addresses in one field',
                                invalid: 'Not an email address',
                                missing: fix === 'hubspot' ? 'No email in HubSpot' : 'No email',
                              }[problem.reason] || problem.reason}
                            </td>
                            <td>
                              {fix === 'hubspot' && 'Fix in HubSpot, then sync'}
                              {fix === 'no_hubspot_record' && 'No HubSpot record'}
                              {fix === 'resync' && index === 0 && (
                                <>
                                  HubSpot is already correct —{' '}
                                  <button
                                    type="button"
                                    className="btn btn-sm btn-secondary"
                                    style={{ padding: '0 6px', fontSize: '12px' }}
                                    disabled={bulkSyncState.isRunning || !canSyncRecord(row)}
                                    onClick={() => {
                                      setEmailProblemsModalOpen(false);
                                      openSyncPreview(row);
                                    }}
                                  >
                                    Sync Record
                                  </button>
                                </>
                              )}
                            </td>
                          </tr>
                        )))}
                      </tbody>
                    </table>
                  </div>
                </div>
                <div className="modal-footer">
                  <button
                    type="button"
                    className="btn btn-secondary"
                    onClick={() => setEmailProblemsModalOpen(false)}
                  >
                    Close
                  </button>
                </div>
              </div>
            </div>
          </div>
          <div className="modal-backdrop show" onClick={() => setEmailProblemsModalOpen(false)} />
        </>
      )}

      {requestLogModalOpen && (
        <>
          <div className="modal show" style={{ display: 'block' }} tabIndex="-1" role="dialog" aria-modal="true">
            <div className="modal-dialog modal-dialog-centered modal-lg" role="document">
              <div className="modal-content">
                <div className="modal-header bg-primary text-white">
                  <h5 className="modal-title">
                    Request log ({requestLog.length} request{requestLog.length === 1 ? '' : 's'})
                  </h5>
                  <button
                    type="button"
                    className="btn-close btn-close-white"
                    onClick={() => setRequestLogModalOpen(false)}
                    aria-label="Close"
                  />
                </div>
                <div className="modal-body">
                  <div style={{ overflowX: 'auto' }}>
                    <table className="table table-sm table-bordered" style={{ fontSize: '12px', marginBottom: 0 }}>
                      <thead>
                        <tr>
                          <th>#</th>
                          <th>Request</th>
                          <th>Status</th>
                          <th>Duration</th>
                          <th>Returned</th>
                        </tr>
                      </thead>
                      <tbody>
                        {requestLog.map((entry) => (
                          <tr
                            key={entry.requestNumber}
                            style={
                              entry.pending ? { backgroundColor: 'rgba(255, 193, 7, 0.1)' }
                              : entry.ok === false ? { backgroundColor: 'rgba(255, 0, 0, 0.08)' }
                              : undefined
                            }
                          >
                            <td>{entry.requestNumber}</td>
                            <td>{entry.label}</td>
                            <td>
                              {entry.pending
                                ? 'Pending...'
                                : entry.ok ? entry.status : `${entry.status || 'ERR'}${entry.error ? ` (${entry.error})` : ''}`}
                            </td>
                            <td>{entry.pending ? '—' : `${entry.durationMs}ms`}</td>
                            <td>{entry.returned ?? '—'}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
                <div className="modal-footer">
                  <button
                    type="button"
                    className="btn btn-secondary"
                    onClick={() => setRequestLogModalOpen(false)}
                  >
                    Close
                  </button>
                </div>
              </div>
            </div>
          </div>
          <div className="modal-backdrop show" onClick={() => setRequestLogModalOpen(false)} />
        </>
      )}

      {loadError && (
        <div className="alert alert-danger mb-3" role="alert">
          {loadError}
        </div>
      )}

    <div className="border bg-white p-3">
      <div className="border" style={{ flexDirection: 'row', display: 'flex' }}>
        <div style={{ padding: '10px', width: '50%' }}>
          <strong>Sync Status:</strong> <span className="text-muted" style={{ fontSize: '12px' }}>(click a status to filter)</span>
          <div
            style={{ display: 'flex', gap: '12px', flexWrap: 'wrap', marginTop: '3px', cursor: 'pointer' }}
            onClick={() => toggleSyncStatusFilter('hs_only')}
            title="Filter to hs_only records"
          >
            <b className='border px-1' style={{ width: '50px', backgroundColor: 'rgba(255, 0, 0, 0.1)', borderRadius: '3px' }}>{statusCounts.hs_only || 0}</b>
            <b className='border px-1' style={{ backgroundColor: 'rgba(255, 0, 0, 0.1)', borderRadius: '3px' }}>hs_only</b> Record exists in Hubspot but not in Fastlane
          </div>
          <div
            style={{ display: 'flex', gap: '12px', flexWrap: 'wrap', marginTop: '3px', cursor: 'pointer' }}
            onClick={() => toggleSyncStatusFilter('ff_only')}
            title="Filter to ff_only records"
          >
            <b className='border px-1' style={{ width: '50px', backgroundColor: 'rgba(255, 255, 0, 0.1)', borderRadius: '3px' }}>{statusCounts.ff_only || 0}</b>
            <b className='border px-1' style={{ backgroundColor: 'rgba(255, 255, 0, 0.1)', borderRadius: '3px' }}>ff_only</b> Record exists in Fastlane but not in Hubspot
          </div>
          <div
            style={{ display: 'flex', gap: '12px', flexWrap: 'wrap', marginTop: '3px', cursor: 'pointer' }}
            onClick={() => toggleSyncStatusFilter('out_of_sync')}
            title="Filter to out_of_sync records"
          >
            <b className='border px-1' style={{ width: '50px', backgroundColor: '#f5f5f5', borderRadius: '3px' }}>{statusCounts.out_of_sync || 0}</b>
            <b className='border px-1' style={{ backgroundColor: '#f5f5f5', borderRadius: '3px' }}>out_of_sync</b> Record exists in both systems but is out of sync
          </div>
          <div
            style={{ display: 'flex', gap: '12px', flexWrap: 'wrap', marginTop: '3px', cursor: 'pointer' }}
            onClick={() => toggleSyncStatusFilter('synced')}
            title="Filter to synced records"
          >
            <b className='border px-1' style={{ width: '50px', backgroundColor: 'white', borderRadius: '3px' }}>{statusCounts.synced || 0}</b>
            <b className='border px-1' style={{ backgroundColor: 'white', borderRadius: '3px' }}>synced</b> Record exists in both systems and is in sync
          </div>
          <div
            style={{ display: 'flex', gap: '12px', flexWrap: 'wrap', marginTop: '3px', cursor: 'pointer' }}
            onClick={() => toggleSyncStatusFilter('ff_disabled_synced')}
            title="Filter to ff_disabled_synced records"
          >
            <b className='border px-1' style={{ width: '50px', backgroundColor: '#eeeeee', borderRadius: '3px' }}>{statusCounts.ff_disabled_synced || 0}</b>
            <b className='border px-1' style={{ backgroundColor: '#eeeeee', borderRadius: '3px' }}>ff_disabled_synced</b> In sync, and inactive in Fastlane / archived in HubSpot
          </div>
        </div>
        <div style={{ padding: '10px', width: '50%' }}>
          <strong>Merged:</strong>
          <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap', marginTop: '3px' }}>
            <b className='border px-1' style={{ borderRadius: '3px' }}>HS Merged IDs</b> Hubspot IDs of records that have been merged into this record.
          </div>
          <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap', marginTop: '3px' }}>
            <b className='border px-1' style={{ borderRadius: '3px' }}>HS Merged Parent ID</b> Hubspot ID of the parent record if this record has been merged into.
          </div>
        </div>
      </div>
      <Table
        loadingFFData={loadingFFData}
        loadingHubspotData={loadingHubspotData}
        data={data}
        columns={columns}
        renderToolbar={(table) => {
          tableInstanceRef.current = table;

          const pageSyncableRecords = table.getRowModel().rows
            .map((row) => row.original)
            .filter(canSyncRecord);

          return (
            <button
              type="button"
              className="btn btn-primary btn-sm"
              disabled={bulkSyncState.isRunning || pageSyncableRecords.length === 0}
              onClick={() => openBulkSyncConfirmation(pageSyncableRecords)}
            >
              {bulkSyncState.isRunning
                ? `Syncing Page (${bulkSyncState.completed}/${bulkSyncState.total})`
                : `Sync This Page (${pageSyncableRecords.length})`}
            </button>
          );
        }}
      />
    </div>

      {bulkSyncConfirmation.isOpen && (
        <>
          <div className="modal show" style={{ display: 'block' }} tabIndex="-1" role="dialog" aria-modal="true">
            <div className="modal-dialog modal-dialog-centered" role="document">
              <div className="modal-content">
                <div className="modal-header">
                  <h5 className="modal-title">Confirm Bulk Sync</h5>
                  <button
                    type="button"
                    className="btn-close"
                    onClick={closeBulkSyncConfirmation}
                    aria-label="Close"
                  />
                </div>
                <div className="modal-body">
                  <p style={{ marginBottom: '12px' }}>
                    Sync {bulkSyncConfirmation.records.length} records from the current page?
                  </p>
                  <p className="text-muted" style={{ marginBottom: 0 }}>
                    This will send one request per row and open the progress modal immediately after you confirm.
                  </p>
                </div>
                <div className="modal-footer">
                  <button type="button" className="btn btn-secondary" onClick={closeBulkSyncConfirmation}>
                    Cancel
                  </button>
                  <button type="button" className="btn btn-primary" onClick={confirmBulkSync}>
                    Start Sync
                  </button>
                </div>
              </div>
            </div>
          </div>
          <div className="modal-backdrop show" onClick={closeBulkSyncConfirmation} />
        </>
      )}

      {bulkSyncState.isOpen && (
        <>
          <div className="modal show" style={{ display: 'block' }} tabIndex="-1" role="dialog" aria-modal="true">
            <div className="modal-dialog modal-dialog-centered modal-lg" role="document">
              <div className="modal-content">
                <div className="modal-header bg-primary text-white">
                  <h5 className="modal-title">Bulk Sync Progress</h5>
                  <button
                    type="button"
                    className="btn-close btn-close-white"
                    onClick={closeBulkSyncModal}
                    aria-label="Close"
                    disabled={bulkSyncState.isRunning}
                  />
                </div>
                <div className="modal-body">
                  <div className="mb-3">
                    <div className="d-flex justify-content-between align-items-center mb-2">
                      <strong>{bulkSyncState.completed} of {bulkSyncState.total} complete</strong>
                      <span>{bulkProgressPercent}%</span>
                    </div>
                    <div className="progress" style={{ height: '12px' }}>
                      <div
                        className="progress-bar"
                        role="progressbar"
                        style={{ width: `${bulkProgressPercent}%` }}
                        aria-valuenow={bulkProgressPercent}
                        aria-valuemin="0"
                        aria-valuemax="100"
                      />
                    </div>
                  </div>

                  <div className="mb-3">
                    <strong>Status:</strong>
                    <div style={{ marginTop: '6px' }}>
                      {bulkSyncState.isRunning
                        ? `Syncing ${bulkSyncState.currentLabel || 'next lead owner'}...`
                        : 'Bulk sync complete.'}
                    </div>
                  </div>

                  <div>
                    <strong>Failures ({bulkSyncState.failures.length})</strong>
                    <div
                      className="border rounded mt-2 p-2"
                      style={{ maxHeight: '260px', overflowY: 'auto', backgroundColor: '#fafafa' }}
                    >
                      {bulkSyncState.failures.length === 0 ? (
                        <div className="text-muted">No failures yet.</div>
                      ) : (
                        bulkSyncState.failures.map((failure) => (
                          <div key={failure.id} className="border-bottom pb-2 mb-2">
                            <div><strong>{failure.label}</strong></div>
                            <div style={{ whiteSpace: 'pre-line' }}>{failure.message}</div>
                          </div>
                        ))
                      )}
                    </div>
                  </div>
                </div>
                <div className="modal-footer">
                  <button
                    type="button"
                    className="btn btn-secondary"
                    onClick={closeBulkSyncModal}
                    disabled={bulkSyncState.isRunning}
                  >
                    Close
                  </button>
                </div>
              </div>
            </div>
          </div>
          <div className="modal-backdrop show" onClick={bulkSyncState.isRunning ? undefined : closeBulkSyncModal} />
        </>
      )}

      {/* Sync Preview Modal */}
      {syncPreview.isOpen && (
        <>
          <div className="modal show" style={{ display: 'block' }} tabIndex="-1" role="dialog" aria-modal="true">
            <div className="modal-dialog modal-dialog-centered modal-lg" role="document">
              <div className="modal-content">
                <div className="modal-header">
                  <h5 className="modal-title">
                    {syncPreview.isNewRecord ? 'Create Lead Owner From HubSpot' : 'Sync Lead Owner With HubSpot'}
                  </h5>
                  <button
                    type="button"
                    className="btn-close"
                    onClick={closeSyncPreview}
                    aria-label="Close"
                    disabled={syncPreview.isSaving}
                  />
                </div>
                <div className="modal-body" style={{ maxHeight: '60vh', overflowY: 'auto' }}>
                  {syncPreview.isLoading && <div className="text-muted">Loading preview...</div>}

                  {syncPreview.error && (
                    <div className="alert alert-danger" role="alert" style={{ whiteSpace: 'pre-line' }}>
                      {syncPreview.error}
                    </div>
                  )}

                  {!syncPreview.isLoading && !syncPreview.error && syncPreview.fields && (
                    <>
                      {!syncPreview.fields.some((field) => field.changed) && (
                        <div className="text-muted mb-2">No changes — this record already matches HubSpot.</div>
                      )}
                      <table className="table table-sm table-bordered" style={{ marginBottom: 0 }}>
                        <thead>
                          <tr>
                            <th>Property</th>
                            <th>Old Value</th>
                            <th>New Value</th>
                          </tr>
                        </thead>
                        <tbody>
                          {syncPreview.fields.map(({ property, from, to, changed }) => (
                            <tr key={property}>
                              <td>{humanizeDiffKey(property)}</td>
                              <td>{formatDiffValue(from)}</td>
                              <td style={changed ? { backgroundColor: 'rgba(40, 167, 69, 0.2)', fontWeight: 600 } : undefined}>
                                {formatDiffValue(to)}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </>
                  )}
                </div>
                <div className="modal-footer">
                  <button
                    type="button"
                    className="btn btn-secondary"
                    onClick={closeSyncPreview}
                    disabled={syncPreview.isSaving}
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    className="btn btn-primary"
                    onClick={confirmSyncPreview}
                    disabled={syncPreview.isLoading || syncPreview.isSaving || Boolean(syncPreview.error) || !(syncPreview.fields || []).some((field) => field.changed)}
                  >
                    {syncPreview.isSaving ? 'Saving...' : 'Save'}
                  </button>
                </div>
              </div>
            </div>
          </div>
          <div className="modal-backdrop show" onClick={syncPreview.isSaving ? undefined : closeSyncPreview} />
        </>
      )}

      {modalMessage && (
        <>
          <div className="modal fade show" style={{ display: 'block' }} tabIndex="-1" role="dialog" aria-modal="true">
            <div className="modal-dialog modal-dialog-centered" role="document">
              <div className="modal-content">
                <div className="modal-header bg-danger text-white">
                  <h5 className="modal-title">Sync Failed</h5>
                  <button
                    type="button"
                    className="btn-close btn-close-white"
                    onClick={() => setModalMessage(null)}
                    aria-label="Close"
                  />
                </div>
                <div className="modal-body">
                  <p style={{ whiteSpace: 'pre-line' }}>{modalMessage}</p>
                </div>
                <div className="modal-footer">
                  <button type="button" className="btn btn-secondary" onClick={() => setModalMessage(null)}>
                    Close
                  </button>
                </div>
              </div>
            </div>
          </div>
          <div className="modal-backdrop fade show" onClick={() => setModalMessage(null)} />
        </>
      )}

      {successToastMessage && (
        <div
          className="alert alert-success shadow"
          role="status"
          style={{
            position: 'fixed',
            right: '16px',
            bottom: '16px',
            margin: 0,
            zIndex: 2000,
            minWidth: '280px',
            maxWidth: '420px',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            gap: '12px',
          }}
        >
          <span>{successToastMessage}</span>
          <button
            type="button"
            className="btn-close"
            aria-label="Close"
            onClick={() => setSuccessToastMessage(null)}
          />
        </div>
      )}
    </>
  );
};

const element = document.getElementById('lead-owners-table');
if (element) {
  const root = createRoot(element);
  root.render(<LeadOwnersTable/>);
}
